import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, BadgeCheck, Calendar, ChevronLeft, ChevronRight, Clock,
  Info, MapPin, ShieldCheck, Sparkles, Users,
} from "lucide-react";
import { useLanguage } from "../i18n";
import { useSEO } from "../seo";
import { PAGE_CONTENT } from "../content-pages";
import { API, IMAGES, LINKS, imgUrl } from "../data";
import { WhatsAppIcon } from "../components/WhatsAppButton";
import EnquiryModal from "../components/EnquiryModal";
import ExperiencesMap from "../components/ExperiencesMap";

const EASE = [0.22, 1, 0.36, 1];
const WHY_ICONS = [Sparkles, Users, BadgeCheck, ShieldCheck];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Carousel({ images, alt, badge, testid }) {
  const [idx, setIdx] = useState(0);
  const go = (d) => (e) => {
    e.stopPropagation();
    setIdx((i) => (i + d + images.length) % images.length);
  };

  return (
    <div data-testid={testid} className="group relative aspect-[4/3] overflow-hidden rounded-[2rem] ring-1 ring-navy/10">
      {images.map((src, i) => (
        <img
          key={src}
          src={imgUrl(src)}
          alt={i === idx ? alt : ""}
          aria-hidden={i !== idx}
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      <span className="absolute left-5 top-5 rounded-full bg-navy/90 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.15em] text-sky backdrop-blur-sm">
        {badge}
      </span>
      {images.length > 1 && (
        <>
          <button
            data-testid={`${testid}-prev`}
            onClick={go(-1)}
            aria-label="Foto precedente"
            className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-navy/70 text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-navy group-hover:opacity-100"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            data-testid={`${testid}-next`}
            onClick={go(1)}
            aria-label="Foto successiva"
            className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-navy/70 text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-navy group-hover:opacity-100"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5">
            {images.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/50"}`} />
            ))}
          </div>
          <span className="absolute bottom-4 right-4 rounded-full bg-navy/70 px-3 py-1 text-[10px] font-extrabold text-white backdrop-blur-sm">
            {idx + 1}/{images.length}
          </span>
        </>
      )}
    </div>
  );
}

export default function ExperiencesPage() {
  const { lang } = useLanguage();
  const e = PAGE_CONTENT[lang].experiences;
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState(null);
  const [dbItems, setDbItems] = useState(null);

  useSEO(
    lang === "en" ? "Authentic experiences in Ogliastra | OGStay" : "Esperienze autentiche in Ogliastra | OGStay",
    lang === "en"
      ? "Boat trips in the Gulf of Orosei, winery tastings and Gennargentu trekking: experiences selected by OGStay's local team."
      : "Barca nel Golfo di Orosei, degustazioni in cantina e trekking sul Gennargentu: esperienze selezionate dal team locale OGStay."
  );

  useEffect(() => {
    axios
      .get(`${API}/experiences`)
      .then((r) => setDbItems(r.data))
      .catch(() => setDbItems([]));
  }, []);

  const list =
    dbItems && dbItems.length
      ? dbItems.map((it) => ({
          title: lang === "en" && it.title_en ? it.title_en : it.title,
          text: lang === "en" && it.text_en ? it.text_en : it.text,
          location: lang === "en" && it.location_en ? it.location_en : it.location,
          duration: lang === "en" && it.duration_en ? it.duration_en : it.duration,
          schedule: lang === "en" && it.schedule_en ? it.schedule_en : it.schedule,
          price: it.price,
          images: it.images && it.images.length ? it.images : [IMAGES.experiences[0]],
        }))
      : e.items.map((item, i) => ({ ...item, images: [IMAGES.experiences[i]] }));

  const waText = (title) =>
    `${LINKS.whatsapp}?text=${encodeURIComponent(
      lang === "en" ? `Hi OGStay! I'd like information about: ${title}` : `Ciao OGStay! Vorrei informazioni su: ${title}`
    )}`;

  return (
    <div data-testid="experiences-page" className="bg-warm">
      {/* Hero */}
      <section className="relative overflow-hidden bg-navy pb-20 pt-40 text-white sm:pb-24 sm:pt-48">
        <img src={IMAGES.experiencesHero} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/55 to-navy" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="kicker text-sky">
            <span className="h-px w-10 bg-sky" />
            {e.kicker}
          </motion.p>
          <motion.h1
            data-testid="experiences-hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.12, ease: EASE }}
            className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
          >
            {e.heroTitle}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.24, ease: EASE }} className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
            {e.heroSub}
          </motion.p>
        </div>
      </section>

      {/* Map */}
      <section className="pt-20 sm:pt-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <ExperiencesMap
              items={list.slice(0, 3)}
              labels={{ from: e.from, person: e.person, cta: e.cta }}
              onEnquire={(title) => setEnquiry(title)}
            />
          </Reveal>
        </div>
      </section>

      {/* Experiences list */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl space-y-20 px-5 sm:px-8 lg:space-y-28">
          {list.map((item, i) => (
            <Reveal key={item.title}>
              <article data-testid={`experience-detail-${i}`} className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                <div className={`${i % 2 ? "lg:order-2" : ""}`}>
                  <Carousel images={item.images} alt={item.title} badge={e.badge} testid={`exp-carousel-${i}`} />
                </div>
                <div className={`${i % 2 ? "lg:order-1" : ""}`}>
                  <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-navy sm:text-4xl">{item.title}</h2>
                  <p className="mt-5 max-w-lg text-base leading-relaxed text-mute">{item.text}</p>
                  <ul className="mt-7 space-y-3.5">
                    <li className="flex items-center gap-3 text-sm font-semibold text-ink">
                      <MapPin className="h-4 w-4 shrink-0 text-med" />
                      {item.location}
                    </li>
                    <li className="flex items-center gap-3 text-sm font-semibold text-ink">
                      <Clock className="h-4 w-4 shrink-0 text-med" />
                      {item.duration}
                    </li>
                    <li className="flex items-center gap-3 text-sm font-semibold text-ink">
                      <Calendar className="h-4 w-4 shrink-0 text-med" />
                      {item.schedule}
                    </li>
                  </ul>
                  <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4">
                    <div>
                      <span className="block text-[11px] font-extrabold uppercase tracking-[0.2em] text-mute">{e.from}</span>
                      <span className="font-display text-4xl font-semibold text-navy">
                        {item.price}
                        <span className="ml-1.5 align-middle text-sm font-semibold text-mute">{e.person}</span>
                      </span>
                      <span className="mt-1 block text-xs font-semibold text-mute">{e.priceNote}</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <button data-testid={`experience-cta-${i}`} onClick={() => setEnquiry(item.title)} className="btn-primary !px-6 !py-3.5 text-[13px]">
                        {e.cta}
                        <ArrowRight className="h-4 w-4" />
                      </button>
                      <a
                        data-testid={`experience-whatsapp-${i}`}
                        href={waText(item.title)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-dark !px-6 !py-3.5 text-[13px]"
                      >
                        <span className="h-4 w-4 text-sky">{WhatsAppIcon}</span>
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why */}
      <section data-testid="experiences-why" className="bg-navy py-24 text-white sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">{e.why.title}</h2>
            <p className="mt-5 text-base leading-relaxed text-white/65">{e.why.sub}</p>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {e.why.items.map((item, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={item.title} delay={i * 0.08}>
                  <div data-testid={`experiences-why-${i}`} className="h-full rounded-[1.75rem] bg-med/40 p-8 ring-1 ring-white/10 transition-colors duration-500 hover:bg-med/60">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky/15 text-sky">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-6 font-display text-xl font-semibold">{item.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-white/60">{item.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2} className="mt-14">
            <div data-testid="experiences-note" className="flex items-start gap-4 rounded-[1.75rem] border border-sky/25 bg-sky/10 p-7">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-sky" />
              <p className="text-sm leading-relaxed text-white/75">{e.note}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mini CTA */}
      <section className="py-24 text-center sm:py-28">
        <Reveal>
          <h2 className="mx-auto max-w-2xl px-5 font-display text-3xl font-semibold leading-tight tracking-tight text-navy sm:text-4xl">
            {PAGE_CONTENT[lang].article.ctaTitle}
          </h2>
          <button data-testid="experiences-final-cta" onClick={() => navigate("/strutture")} className="btn-dark mt-9">
            {PAGE_CONTENT[lang].article.cta}
            <ArrowRight className="h-4 w-4" />
          </button>
        </Reveal>
      </section>

      <EnquiryModal open={!!enquiry} onOpenChange={() => setEnquiry(null)} subject={enquiry} />
    </div>
  );
}
